import React, { createContext, useContext } from "react";

import ConfigService from "../services/ConfigService";

import PageNotFound from "../components/pageNotFound/PageNotFound";
import LoadingPage from "../components/loadingPage/LoadingPage";
import { setPromoColors, useQueryWithNoCache } from "./utils";
import { isSweepOrg } from "../utils/sweepUtils";
import { isSoccerbetOrg } from "../utils/brandUtils";
import { getFontUrl } from "../utils/fontUrl";

import i18n from "../i18n";

const ConfigContext = createContext(null);

const setCSSVariables = (paramKey: string, paramValue: string) => {
  document.documentElement.style.setProperty(`${paramKey}`, `${paramValue}`);
};

const setPromoBackground = (bgString: string | undefined) => {
  if (!bgString || bgString.length === 0) {
    return;
  }

  const bgObj = JSON.parse(bgString);
  setCSSVariables("--background-image-web", `url(${bgObj?.["desktop"]?.[0]?.uri})`);
  setCSSVariables("--background-image-mobile", `url(${bgObj?.["mobile"]?.[0]?.uri})`);
};

const getColorVariable = (varName: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(varName);
};

const getIsLeadingFont = (fontStack: string, fontName: string) =>
  fontStack.split(",")[0]?.trim() === fontName;

const updateFontVariables = (fontWeight: string, fontName: string) => {
  const currentFonts = getColorVariable(`--font-${fontWeight}`);
  if (getIsLeadingFont(currentFonts, fontName)) return;
  const newFontFace = `${fontName}, ${currentFonts}`;
  document.documentElement.style.setProperty(`--font-${fontWeight}`, newFontFace);
};

const updateTitleFontVariables = (fontWeight: string, fontName: string) => {
  const currentFonts = getColorVariable(`--title-font-${fontWeight}`);
  if (getIsLeadingFont(currentFonts, fontName)) return;
  const newFontFace = `${fontName}, ${currentFonts || getColorVariable(`--font-${fontWeight}`)}`;
  document.documentElement.style.setProperty(`--title-font-${fontWeight}`, newFontFace);
};

const FONT_FORMATS: Record<string, string> = {
  woff2: "woff2",
  woff: "woff",
  ttf: "truetype",
  otf: "opentype",
};

const getFontFormat = (fontPath: string) => {
  const ext = fontPath?.split("?")?.[0]?.split(".")?.pop()?.toLowerCase() || "";
  const format = FONT_FORMATS[ext];
  return format ? ` format('${format}')` : "";
};

const addedFontFaces = new Set<string>();

const createFontFaceStyle = (fontPath: string, fontWeight: string, isTitle = false) => {
  const fontName = (fontPath as any)?.split("/")?.pop()?.split(".")?.[0];
  const fontFaceKey = `${fontWeight?.toLowerCase()}|${fontPath}`;
  if (!addedFontFaces.has(fontFaceKey)) {
    addedFontFaces.add(fontFaceKey);
    const fontFaceStyleElement = document.createElement("style");
    fontFaceStyleElement.innerHTML = `
    @font-face {
        font-family: ${fontName};
        font-weight: ${fontWeight?.toLowerCase()};
        font-display: swap;
        src: url('${getFontUrl(fontPath)}')${getFontFormat(fontPath)};
      }
    `;
    document.head.appendChild(fontFaceStyleElement);
  }

  if (isTitle) {
    updateTitleFontVariables(fontWeight?.toLowerCase(), fontName);
  } else {
    updateFontVariables(fontWeight?.toLowerCase(), fontName);
  }
};

export const ConfigProvider = ({
  children,
  promoId,
  loadingFallback,
  errorFallback,
}: {
  children: React.ReactNode;
  promoId?: string;
  loadingFallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
}) => {
  const promo_id = promoId || ConfigService.getPromoId();
  const {
    data: promoConfig,
    isLoading,
    isError,
  } = useQueryWithNoCache(
    ["promoConfig", promo_id],
    `${import.meta.env.VITE_WIDGET_CDN_BASE_URL}/promo/${promo_id}/config.json`,
    {
      select: (promoConfig) => promoConfig?.data,
    }
  );

  if (
    !isLoading &&
    (isError || !promoConfig || typeof promoConfig !== "object" || promoConfig === null)
  ) {
    return <>{errorFallback ?? <PageNotFound />}</>;
  }
  if (isLoading) {
    return <>{loadingFallback ?? <LoadingPage />}</>;
  }

  async function loadFonts() {
    const lang = i18n.language;
    let selectedLang;
    let selectedTitleLang;

    if (Array.isArray(promoConfig?.fonts) && promoConfig?.fonts?.length > 0) {
      if (lang) {
        selectedLang = promoConfig.fonts.find((font: any) => font.lang === lang);
      }
      if (!selectedLang) {
        selectedLang = promoConfig.fonts.find((font: any) => font.default);
      }
      if (!selectedLang) {
        selectedLang = promoConfig.fonts[0];
      }

      const fontFaceStyles = Object.entries(selectedLang?.fontFamily ?? {});
      fontFaceStyles.forEach(([fontWeight, fontPath]) => {
        createFontFaceStyle(typeof fontPath === "string" ? fontPath : "", fontWeight);
      });
    }

    if (Array.isArray(promoConfig?.titleFont) && promoConfig?.titleFont?.length > 0) {
      if (lang) {
        selectedTitleLang = promoConfig.titleFont.find((font: any) => font.lang === lang);
      }
      if (!selectedTitleLang) {
        selectedTitleLang = promoConfig.titleFont.find((font: any) => font.default);
      }
      if (!selectedTitleLang) {
        selectedTitleLang = promoConfig.titleFont[0];
      }

      const titleFontFaceStyles = Object.entries(selectedTitleLang?.fontFamily ?? {});
      titleFontFaceStyles.forEach(([fontWeight, fontPath]) => {
        createFontFaceStyle(typeof fontPath === "string" ? fontPath : "", fontWeight, true);
      });
    }
  }

  loadFonts();

  setPromoColors(promoConfig.layout);
  setPromoBackground(promoConfig.components.background);

  const contextValue = {
    ...promoConfig,
    isSweep: isSweepOrg(promoConfig?.orgUuid),
    isSoccerbet: isSoccerbetOrg(promoConfig?.orgUuid),
  };

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};

export const useConfig = () => {
  return useContext(ConfigContext);
};

export { ConfigContext };
